import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { EditSheet, DangerAction, changedFields, toField } from '../ui/EditSheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { queryState, refreshFailed } from '../ui/QueryStates';
import { Field, field } from '../create/Field';

/**
 * Add and fix the shop's own vans and tools (107).
 *
 * Two sheets over one set of fields, which is how the rest of the app is built: a create
 * sheet is a plain `Sheet` with its own verb on the button (NewCustomerSheet), and an edit
 * sheet is an `EditSheet` — Save disabled until something changed, the server's complaint
 * above the fields, the retire action separated below a rule, and closing over unsaved
 * typing asks first. Sharing `AssetFields` is what keeps the two from drifting into
 * different ideas of what a van has.
 */

type Draft = {
  name: string;
  plate: string;
  make: string;
  model: string;
  capacityNote: string;
  homeBaseAddress: string;
  stockLocationId: string;
  serialNo: string;
  category: string;
};

const EMPTY: Draft = {
  name: '',
  plate: '',
  make: '',
  model: '',
  capacityNote: '',
  homeBaseAddress: '',
  stockLocationId: '',
  serialNo: '',
  category: '',
};

/**
 * The fields, once. `kind` decides which half is asked for: a van has a plate and a home
 * base, a tool has a serial and a category, and asking a van for its serial number is how
 * a form teaches people to ignore it.
 */
function AssetFields({
  kind,
  draft,
  set,
  vans,
  vansIncomplete,
  onRetryVans,
}: {
  kind: 'vehicle' | 'tool';
  draft: Draft;
  set: (k: keyof Draft) => (v: string) => void;
  /** Vehicle stock locations this asset may bridge to, plus whichever it already holds. */
  vans: { id: string; name: string }[];
  /** The locations query has not answered — the picker is short and must say so. */
  vansIncomplete?: boolean;
  onRetryVans?: () => void;
}) {
  return (
    <>
      <Field label="Name">
        <input
          className={field}
          value={draft.name}
          onChange={(e) => set('name')(e.target.value)}
          placeholder={kind === 'vehicle' ? 'Unit 12 — Transit 250' : 'Recovery machine'}
          autoFocus
        />
      </Field>

      {kind === 'vehicle' ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Plate">
              <input
                className={field}
                value={draft.plate}
                onChange={(e) => set('plate')(e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Make">
              <input
                className={field}
                value={draft.make}
                onChange={(e) => set('make')(e.target.value)}
                placeholder="Ford"
              />
            </Field>
            <Field label="Model">
              <input
                className={field}
                value={draft.model}
                onChange={(e) => set('model')(e.target.value)}
                placeholder="Transit 250"
              />
            </Field>
            <Field label="Capacity note">
              <input
                className={field}
                value={draft.capacityNote}
                onChange={(e) => set('capacityNote')(e.target.value)}
                placeholder="Ladder rack, no lift"
              />
            </Field>
          </div>
          <Field label="Home base">
            <input
              className={field}
              value={draft.homeBaseAddress}
              onChange={(e) => set('homeBaseAddress')(e.target.value)}
              placeholder="400 Depot Rd, Lewis Center, OH"
            />
          </Field>
          <Field label="Parts location">
            <select
              className={field}
              value={draft.stockLocationId}
              onChange={(e) => set('stockLocationId')(e.target.value)}
            >
              <option value="">Not linked</option>
              {vans.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          {vansIncomplete ? (
            <Notice tone="warn">
              <span className="flex flex-wrap items-center gap-2">
                <span>
                  We couldn&apos;t load your parts locations just now, so this list is
                  short rather than wrong. Anything already linked is still shown.
                </span>
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={onRetryVans}
                >
                  Try again
                </Button>
              </span>
            </Notice>
          ) : null}
          {/* The bridge, said in words: this is why a van appears in two places at once. */}
          <p className="text-xs text-muted-foreground">
            Linking a parts location ties this vehicle to the van stock already counted in
            Inventory. One vehicle per location — the shop's parts stay where they are.
          </p>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Serial number">
            <input
              className={field}
              value={draft.serialNo}
              onChange={(e) => set('serialNo')(e.target.value)}
            />
          </Field>
          <Field label="Category">
            <input
              className={field}
              value={draft.category}
              onChange={(e) => set('category')(e.target.value)}
              placeholder="Diagnostics"
            />
          </Field>
        </div>
      )}
    </>
  );
}

/**
 * Vehicle stock locations, plus `keep` so an edit never silently drops its own link.
 *
 * `enabled` because only a VEHICLE can name a parts location: opening a tool's edit
 * sheet was fetching the shop's whole location tree to populate a select that never
 * renders.
 */
function useVanLocations(enabled: boolean, keep?: { id: string; name: string } | null) {
  const trpc = useTRPC();
  const locations = useQuery({ ...trpc.inventory.locations.queryOptions(), enabled });
  const vans = (locations.data ?? [])
    .filter((l) => l.kind === 'vehicle' && l.active)
    .map((l) => ({ id: l.id, name: l.name }));
  if (keep && !vans.some((v) => v.id === keep.id)) vans.unshift(keep);
  // ⚠️ `?? []` alone renders a FAILED query as a shop with no vans — a select
  // confidently offering "Not linked" and nothing else, which is the same
  // failed-query-as-no-data defect already fixed one file over for `org.members`. The
  // picker still opens (whatever is already linked is in the list, so an edit is never
  // blocked by it) but it has to admit the list is short.
  //
  // ⚠️ …and only when it IS short. `!locations.data` is also true for the ordinary half
  // second before the query answers, so the first spelling of this flashed "we couldn't
  // load your parts locations" on every single open — a warning that cries wolf is worse
  // than none — while saying nothing at all when cached data sat behind a failed
  // refresh. The phase vocabulary answers both halves.
  const phase = queryState(locations);
  return {
    vans,
    incomplete:
      enabled &&
      (phase === 'error' ||
        phase === 'offline' ||
        refreshFailed(locations, locations.data)),
    // ⚠️ A failure state without a way out is only half honest, and this one has no
    // other door: `NewAssetSheet` stays mounted while closed, so closing and reopening
    // does not remount the query and the picker stays short for the rest of the session.
    retry: () => void locations.refetch(),
  };
}

export function NewAssetSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (assetId: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'vehicle' | 'tool'>('vehicle');
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const { vans, incomplete, retry } = useVanLocations(kind === 'vehicle');
  const set = (k: keyof Draft) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const create = useMutation(trpc.asset.create.mutationOptions());

  const submit = async () => {
    const row = await create.mutateAsync({
      kind,
      name: draft.name.trim(),
      // The vehicle half and the tool half are sent separately, so a van that once had a
      // serial typed into it does not carry that field around forever.
      ...(kind === 'vehicle'
        ? {
            plate: draft.plate.trim() || undefined,
            make: draft.make.trim() || undefined,
            model: draft.model.trim() || undefined,
            capacityNote: draft.capacityNote.trim() || undefined,
            homeBaseAddress: draft.homeBaseAddress.trim() || undefined,
            ...(draft.stockLocationId ? { stockLocationId: draft.stockLocationId } : {}),
          }
        : {
            serialNo: draft.serialNo.trim() || undefined,
            category: draft.category.trim() || undefined,
          }),
      ...idem(),
    });
    await qc.invalidateQueries();
    setDraft(EMPTY);
    setKind('vehicle');
    onCreated?.(row.id);
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New asset"
      footer={
        <Button
          className="w-full"
          disabled={draft.name.trim().length === 0 || create.isPending}
          onClick={() => void submit().catch(() => undefined)}
        >
          {create.isPending ? 'Saving…' : 'Add asset'}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}
        <Field label="Kind">
          <select
            className={field}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'vehicle' | 'tool')}
          >
            <option value="vehicle">Vehicle</option>
            <option value="tool">Tool</option>
          </select>
        </Field>
        <AssetFields
          kind={kind}
          draft={draft}
          set={set}
          vans={vans}
          vansIncomplete={incomplete}
          onRetryVans={retry}
        />
        {/* Kind is asked once, here: a van does not become a drain machine, and letting
            an edit change it would leave the other half's fields stranded on the row. */}
        <p className="text-xs text-muted-foreground">
          Only the name is required, and only the kind can&apos;t be changed later.
        </p>
      </div>
    </Sheet>
  );
}

export function EditAssetSheet({
  open,
  onClose,
  asset,
  onArchived,
}: {
  open: boolean;
  onClose: () => void;
  asset: {
    id: string;
    kind: string;
    name: string;
    status: string;
    plate: string | null;
    make: string | null;
    model: string | null;
    capacityNote: string | null;
    homeBaseAddress: string | null;
    stockLocationId: string | null;
    stockLocation: { id: string; name: string } | null;
    serialNo: string | null;
    category: string | null;
  };
  onArchived?: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();

  const original: Draft & { status: string } = {
    name: asset.name,
    plate: toField(asset.plate),
    make: toField(asset.make),
    model: toField(asset.model),
    capacityNote: toField(asset.capacityNote),
    homeBaseAddress: toField(asset.homeBaseAddress),
    stockLocationId: toField(asset.stockLocationId),
    serialNo: toField(asset.serialNo),
    category: toField(asset.category),
    status: asset.status,
  };
  const [draft, setDraft] = useState(original);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const { vans, incomplete, retry } = useVanLocations(
    asset.kind === 'vehicle',
    asset.stockLocation,
  );
  const changed = changedFields(original, draft);
  const dirty = Object.keys(changed).length > 0;

  const save = useMutation(
    trpc.asset.update.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );
  const archive = useMutation(
    trpc.asset.archive.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onArchived?.();
      },
    }),
  );

  const submit = () => {
    const { stockLocationId, status, ...rest } = changed;
    save.mutate({
      assetId: asset.id,
      ...rest,
      // ⚠️ Cleared means CLEARED, and the wire word for that is `null` — an empty string
      // is not a uuid and would come back as a validation error on a form the shop
      // thinks it just emptied.
      ...(stockLocationId !== undefined
        ? { stockLocationId: stockLocationId || null }
        : {}),
      ...(status !== undefined ? { status: status as 'active' | 'out_of_service' } : {}),
      ...idem(),
    });
  };

  return (
    <EditSheet
      open={open}
      onClose={onClose}
      title={`Edit ${asset.name}`}
      dirty={dirty}
      saving={save.isPending}
      error={save.error ?? archive.error}
      onSave={submit}
      destructive={
        asset.status === 'retired' ? null : (
          <DangerAction
            label="Retire this asset"
            question={`Retire ${asset.name}?`}
            detail="It comes off the working register and its current assignments end today. Its maintenance history stays exactly as it is, and you can bring it back."
            pending={archive.isPending}
            onConfirm={() => archive.mutate({ assetId: asset.id, ...idem() })}
          />
        )
      }
    >
      {asset.status === 'retired' ? (
        <Notice tone="info">
          This one is retired. Its history stays here — that is the whole reason it was
          written down. Setting it back in service brings it onto the register again.
        </Notice>
      ) : null}
      <Field label="Status">
        <select
          className={field}
          value={draft.status}
          onChange={(e) => set('status')(e.target.value)}
        >
          <option value="active">In service</option>
          <option value="out_of_service">Out of service</option>
          {/* Present only when it is already the answer: retiring is the DangerAction
              above, which also ends the assignments — a status picker cannot do that, and
              the server refuses `retired` here for exactly that reason. Without this
              option a retired asset would open its own edit form already dirty. */}
          {asset.status === 'retired' ? <option value="retired">Retired</option> : null}
        </select>
      </Field>
      <AssetFields
        kind={asset.kind === 'tool' ? 'tool' : 'vehicle'}
        draft={draft}
        set={set}
        vans={vans}
        vansIncomplete={incomplete}
        onRetryVans={retry}
      />
      <p className="text-xs text-muted-foreground">
        Emptying a box clears it. Anything you don&apos;t touch stays as it is.
      </p>
    </EditSheet>
  );
}
